import { useEffect, useState } from 'react';

import { filterOlderEntries } from '../../utils/filter-older-entries';
import { getObjectFromSessionStorage, getSessionStorageItem, setSessionStorageItem } from '../../utils/session-storage';

/**
 * A custom React hook for form persistence.
 *
 * This hook allows you to persist form data in the browser's sessionStorage and retrieve it on subsequent visits or page reloads.
 *
 * @param {string} eventId - The id of the event to which the data belongs
 * @param {string} storageKey - The key used to store the form data in sessionStorage.
 * @param {object} initialData - The initial form data.
 * @returns {object} - An object containing the formData, and handleChange function.
 *
 * @example
 * const { formData, handleChange } = useFormPersistence('event1', 'storageKey', {
 *   name: '',
 *   email: '',
 * });
 */
export function useFormPersistence<Type extends object>(eventId: string, initialData: Type, storageKey: string) {
  const [formData, setFormData] = useState<Type & { timeStamp: number }>(
    getObjectFromSessionStorage({ ...initialData, timeStamp: Date.now() }, storageKey, eventId)
  );

  useEffect(() => {
    try {
      const currentData = getSessionStorageItem(storageKey);
      const parsedData = currentData ? JSON.parse(currentData) : {};
      const currentTime = Date.now();

      parsedData[eventId] = {
        ...formData,
        timeStamp: currentTime,
      };

      const filteredViewerInformation = filterOlderEntries(parsedData, currentTime, 48);
      setSessionStorageItem(storageKey, JSON.stringify(filteredViewerInformation));
    } catch {
      return;
    }
  }, [formData, storageKey, eventId, initialData]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  return { formData, handleChange };
}
